import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useEffect } from "react";
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  ClipboardCheck,
  BarChart3,
  ShieldCheck,
  Plug,
  SlidersHorizontal,
  CreditCard,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import Logo from "../Logo";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}
interface NavGroup {
  label: string;
  subtitle: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Content",
    subtitle: "Build brands and plan campaigns",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/brands", label: "Brands & Campaigns", icon: Building2 },
      { to: "/calendar", label: "Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Review",
    subtitle: "Approve and publish posts",
    items: [{ to: "/review", label: "Review Queue", icon: ClipboardCheck }],
  },
  {
    label: "Insights",
    subtitle: "Track performance and engagement",
    items: [{ to: "/analytics", label: "Analytics", icon: BarChart3 }],
  },
  {
    label: "Account",
    subtitle: "Security, platforms, and billing",
    items: [
      { to: "/settings/security", label: "Security", icon: ShieldCheck },
      {
        to: "/settings/platforms",
        label: "Connected Platforms",
        icon: Plug,
      },
      {
        to: "/settings/preferences",
        label: "Preferences",
        icon: SlidersHorizontal,
      },
      { to: "/settings/team", label: "Team", icon: Users },
      { to: "/billing", label: "Billing", icon: CreditCard },
    ],
  },
];

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useClerk();
  const { user } = useUser();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    onClose?.();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const email =
    user?.primaryEmailAddress?.emailAddress ??
    user?.emailAddresses?.[0]?.emailAddress ??
    "";

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between px-6 py-[22px] border-b border-[#ECECF2]">
        <Logo size={34} />
        {/* Close button — mobile only */}
        <button
          onClick={onClose}
          className="lg:hidden p-1 -mr-1 text-faint hover:text-slate"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-4 py-5 overflow-y-auto">
        {NAV_GROUPS.map((group, groupIndex) => (
          <div key={group.label} className={groupIndex > 0 ? "mt-[26px]" : ""}>
            <div className="px-2.5 mb-1.5">
              <p className="text-xs font-extrabold uppercase tracking-[0.08em] text-brand-500">
                {group.label}
              </p>
              <p className="text-[13px] text-faint mt-0.5">{group.subtitle}</p>
            </div>

            <div className="space-y-1 mt-1.5">
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-2.5 py-[9px] rounded-[10px] text-[15px] transition-colors ${
                      isActive
                        ? "bg-brand-50 text-ink font-extrabold [&_svg]:text-brand-500"
                        : "text-[#3C3E52] font-bold hover:bg-[#F4F4F9] [&_svg]:text-[#6B6E85]"
                    }`
                  }
                >
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div className="px-6 py-[18px] border-t border-[#ECECF2] flex flex-col gap-2.5">
        <p className="text-sm text-[#6B6E85] truncate">{email}</p>
        <button
          onClick={handleLogout}
          className="text-sm font-bold text-brand-500 hover:text-brand-600 transition-colors text-left"
        >
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — always visible */}
      <aside className="hidden lg:flex w-[300px] flex-shrink-0 bg-white border-r border-[#ECECF2] flex-col">
        {sidebarContent}
      </aside>

      {/* Mobile overlay + drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40"
            onClick={onClose}
            aria-hidden
          />
          {/* Drawer */}
          <aside className="relative w-[300px] max-w-[85vw] bg-white flex flex-col shadow-xl">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
